import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { generateId, generateAriaLabel, generateAriaDescription } from '@/lib/utils/accessibility';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  hint?: string;
  label?: string;
  hideLabel?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, hint, label, hideLabel = false, id, required, ...props }, ref) => {
    const inputId = id || generateId('input');
    const errorId = error ? `${inputId}-error` : undefined;
    const hintId = hint ? `${inputId}-hint` : undefined;
    
    const ariaLabel = label ? generateAriaLabel(label, { required, invalid: !!error }) : undefined;
    const ariaDescription = generateAriaDescription(error, hint);
    
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

    return (
      <div className="space-y-1">
        {label && !hideLabel && (
          <label 
            htmlFor={inputId}
            className={cn(
              'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
              error && 'text-destructive'
            )}
          >
            {label}
            {required && (
              <span className="text-destructive ml-1" aria-label="required">
                *
              </span>
            )}
          </label>
        )}
        
        {hint && (
          <p 
            id={hintId}
            className="text-sm text-muted-foreground"
          >
            {hint}
          </p>
        )}
        
        <input
          type={type}
          id={inputId}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-destructive focus-visible:ring-destructive',
            className
          )}
          ref={ref}
          aria-label={hideLabel ? ariaLabel : undefined}
          aria-describedby={describedBy}
          aria-invalid={error ? 'true' : undefined}
          required={required}
          {...props}
        />
        
        {error && (
          <p 
            id={errorId}
            className="text-sm text-destructive"
            role="alert"
            aria-live="polite"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
